import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { trackDestinationUpdated } from '../../analytics/analytics';
import DestinationDetailsModal from "./DestinationDetailsModal";
import NotFoundPage from "../common/NotFoundPage";
import { isValidRouteSubpage } from "../routes/routeSubpages";
import { Route, ConnectionConfiguration, Destination, Organization, buildDestination } from "../common/ObjectTypes";
import { addRouteDestinations, createRoute, getRoutesList, getRoutesByIntegration, listRoutes, testConnectionAction } from "../../api/routes/routesApi";
import { routeRecordId } from "../routes/routeDisplayName";
import { validateBaseUrl } from "./validateBaseUrl";
import AddToRouteDrawer from "./AddToRouteDrawer";
import ModalShell from "../common/ModalShell";
import UnsavedSectionPrompt, { PendingEdits, SectionPrompt } from "../common/configPanel/UnsavedSectionPrompt";
import { buttonClasses } from "../common/tailwindClassnames";
import { getDestinationDetails, updateDestination } from "../../api/destinations/destinationsApi";
import { toast } from "react-toastify";
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from "../../api/apiUtils";
import { useParams, useLocation } from "react-router-dom";
import { useKeycloakToken } from "../common/useKeycloak";
import { useTranslation } from "react-i18next";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import { isEarthRangerDestinationType, extractEarthRangerSiteName, constructEarthRangerUrl } from "./earthRangerUrl";
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";

export function ManageDestinationDetails() {
    const { keycloak } = useKeycloak();
    const { t } = useTranslation();
    const [token] = useKeycloakToken();
    const { dest_id, subpage } = useParams();
    const location = useLocation();
    // Determine subpage from the URL path
    const isConfig = location.pathname.endsWith('/configuration');
    const currentSubPage = isConfig ? 'configuration' : 'details';
    const navigate = useNavigate();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [destination, setDestination] = useState<Destination>(buildDestination())
    const [destinationTitle, setDestinationTitle] = useState<string>("")
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [orderingString, setOrderingString] = useState("");
    const [loadingRoutes, setLoadingRoutes] = useState(true);
    const [destRoutesFailed, setDestRoutesFailed] = useState(false);
    const [selectedDestRoutes, setSelectedDestRoutes] = useState<Route[]>([]);
    const [changesMade, setChangesMade] = useState(false);
    // What the configuration panel currently has unsaved. The tabs and the links
    // out of this page are here, not in the panel, so they ask it before moving.
    const pendingEditsRef = useRef<PendingEdits | null>(null);
    const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(null);
    // Any navigation that would unmount the configuration panel and take its
    // unsaved edits with it. Routed through the same Save-or-discard question the
    // panel asks when you switch sections inside it.
    const guardedGo = (go: () => void) => {
        const pending = pendingEditsRef.current;
        if (!pending?.hasEdits()) return go();
        setSectionPrompt({
            onSave: () => pending.save(),
            onDiscard: () => pending.discard(),
            proceed: go,
        });
    };
    // Every Route this record provides data to (a Connection can feed many).
    const [providerRoutes, setProviderRoutes] = useState<any[]>([]);
    // Loaded flag: before it settles, "no provider Route" means "unknown", and
    // means "unknown", and offering to create one would let the user make a duplicate.
    const [providerRoleLoaded, setProviderRoleLoaded] = useState(false);
    // The lookup answered, but with a failure: "no Routes" is unknown, so the
    // section says so instead of offering to create a duplicate.
    const [providerRoleFailed, setProviderRoleFailed] = useState(false);
    const [confirmDataSource, setConfirmDataSource] = useState(false);
    const [creatingDataSource, setCreatingDataSource] = useState(false);
    // "Add to a Route": pick an existing Route and this record joins its
    // destinations. Candidates come from the same Routes list the portal
    // already uses, defaulting to the record's workspace.
    const [routeDrawerOpen, setRouteDrawerOpen] = useState(false);
    const [routeCandidates, setRouteCandidates] = useState<any[]>([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [candidatesHaveMore, setCandidatesHaveMore] = useState(false);
    const [drawerWorkspaceFilter, setDrawerWorkspaceFilter] = useState<{ label: string, value: string } | null>(null);
    const [drawerSearch, setDrawerSearch] = useState("");
    const [testActionLoading, setTestActionLoading] = useState(false);
    const [testActionData, setTestActionData] = useState<any>(null);
    const [errors, setErrors] = useState({});
    const [totalCount, setTotalCount] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [orgSearchInput, setOrgSearchInput] = useState<string>("");
    const [reloadDestination, setReloadDestination] = useState<boolean>(false);
    const isEarthRanger = isEarthRangerDestinationType(destination.type.name);
    // Ref for scrollable content
    const scrollableContentRef = useRef<HTMLDivElement | null>(null);

    // Closing the configuration screen unmounts the panel, so it asks first for
    // the same reason a tab switch does.
    const onToggleModal = () => guardedGo(() => {
        const pathArray = window.location.pathname.split('/').filter(Boolean);

        // Canonical routes: /connections/:dest_id[/configuration]
        const destinationsIndex = pathArray.indexOf('connections');
        if (destinationsIndex > -1) {
            // If on configuration, go back to /destinations/:dest_id
            if (pathArray[pathArray.length - 1] === 'configuration' && pathArray.length >= destinationsIndex + 2) {
                const destId = pathArray[destinationsIndex + 1];
                navigate(`/connections/${destId}`);
                return;
            }
            // Otherwise go back to the Connections list
            navigate('/connections');
            return;
        }

        // Legacy routes: /destination/:dest_id and nested /.../destination/:dest_id
        const destinationIndex = pathArray.indexOf('destination');
        if (destinationIndex > -1) {
            const newPathArray = pathArray.slice(0, destinationIndex);
            const newPath = `/${newPathArray.join('/')}` || '/';
            navigate(newPath);
        }
    });

    const loadOrganizationsData = async (search_str: string = "") => {
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn || !keycloak.token) return;
        try {
            if (search_str) {
                const [orgs, , error] = await loadOrganizations(keycloak.token, 0, search_str, []);
                if (!error) setOrganizations(orgs);
            } else {
                let accumulated: Organization[] = [];
                let offset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(keycloak.token, offset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setOrganizations(accumulated);
            }
        } catch (err) {
            console.log('Failed to load organizations:', err);
        }
    };

    useEffect(() => {
        // An invalid :subpage renders the 404 page — skip the fetch cascade
        if (!isValidRouteSubpage(subpage)) return;
        const loadSelectedDestinationRoutes = async () => {
            // Check if user is authenticated before making API calls
            const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
            if (!isLoggedIn) {
                return;
            }

            if (keycloak.token) {
                try {
                    // Route records, not connections: each row is one route with
                    // its own URL, decoupled from the provider integration.
                    const temp_routes = await getRoutesByIntegration(keycloak.token, "destination", dest_id || "", {
                        ordering: orderingString,
                        offset: offset,
                    });
                    setSelectedDestRoutes(temp_routes.results);
                    setTotalCount(temp_routes.count);
                    // Same distinction as the provider role: a failed lookup is not
                    // "this Connection receives from nothing".
                    setDestRoutesFailed(!temp_routes.ok);
                    setLoadingRoutes(false);
                    setPageLoaded(true);
                } catch (err) {
                    console.log(err)
                }
            }
        };

        const loadProviderRole = async () => {
            if (!keycloak.token || !dest_id) return;
            // A Connection can provide data to any number of Routes. Only mark the
            // role resolved when the lookup succeeded: treating a failed request as
            // "no routes" offers Create Route for a provider that already has one,
            // which is how duplicates (and orphans — GUNDI-5428) get made.
            const data = await getRoutesByIntegration(keycloak.token, "provider", dest_id);
            setProviderRoutes(data.results);
            setProviderRoleFailed(!data.ok);
            setProviderRoleLoaded(true);
        };
        loadProviderRole();

        const loadDestinationDetails = async () => {
            // Check if user is authenticated before making API calls
            const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
            if (!isLoggedIn) {
                return;
            }

            if (keycloak.token && dest_id) {
                try {
                    const temp = await getDestinationDetails(keycloak.token, dest_id);
                    // Check if temp is defined and has required properties
                    if (!temp || !temp.owner || !temp.owner.id) {
                        return;
                    }
                    const temp_user = await loadCurrentUserDetails(temp.owner.id, keycloak.token);
                    setCurrentMemberIsAdmin(temp_user);
                    loadSelectedDestinationRoutes();
                    // Use temp.type.name directly — the component-level isEarthRanger is stale here
                    // since destination.type.name is "" until this effect sets it.
                    if (isEarthRangerDestinationType(temp.type.name)) {
                        setDestination({ ...temp, base_url: extractEarthRangerSiteName(temp.base_url) });
                    } else {
                        setDestination(temp);
                    }
                    setDestinationTitle(temp.name);
                    // Reset only on success (not at the top of the try): an eager reset
                    // flashes the loading state over the 404 page while a retry runs
                    setNotFound(false);
                    if (reloadDestination) setReloadDestination(false);
                } catch (err: any) {
                    // 403 is treated like 404: the entity is not viewable by this user
                    if (err?.response?.status === 404 || err?.response?.status === 403) {
                        setReloadDestination(false);
                        setNotFound(true);
                        return;
                    }
                    // Silently handle errors - they're already handled by the interceptor
                    console.log('Failed to load destination details:', err);
                }
            }
        };

        loadDestinationDetails();
        if (!pageLoaded) loadOrganizationsData(); // load once on mount; orgs are stable for the session
    }, [reloadDestination, dest_id, keycloak.token, offset, orderingString, subpage]);

    const candidateRequestId = useRef(0);
    const loadRouteCandidates = async (search: string, workspace: { label: string, value: string } | null) => {
        if (!keycloak.token) return;
        const request = ++candidateRequestId.current;
        setLoadingCandidates(true);
        try {
            // Route RECORDS, not connections: a connection entry describes the
            // provider and carries the UNION of destinations across all its Routes,
            // so filtering on it hid eligible Routes and attaching used whichever
            // Route happened to be first. A record knows its own destinations.
            const data = await listRoutes(keycloak.token, { search, ordering: "name" });
            // The routes endpoint has no owner filter, so the workspace narrowing
            // happens here on the page that was fetched.
            const inWorkspace = workspace
                ? data.results.filter((r: any) => r?.owner === workspace.value || r?.owner?.id === workspace.value)
                : data.results;
            // A Route can't receive data from its own provider, and re-adding a
            // destination it already has would be a no-op — hide both.
            const eligible = inWorkspace.filter((r: any) =>
                !(r?.data_providers || []).some((p: any) => p?.id === dest_id)
                && !(r?.destinations || []).some((d: any) => d?.id === dest_id));
            if (request !== candidateRequestId.current) return;
            setRouteCandidates(eligible);
            setCandidatesHaveMore(!!data.next);
        } catch (err) {
            console.log(err);
        } finally {
            if (request === candidateRequestId.current) setLoadingCandidates(false);
        }
    };

    const openRouteDrawer = () => {
        const ws = destination.owner?.id ? { label: destination.owner.name, value: destination.owner.id } : null;
        setDrawerWorkspaceFilter(ws);
        setDrawerSearch("");
        setRouteDrawerOpen(true);
        loadRouteCandidates("", ws);
    };

    // `route` here is a route RECORD from the picker, so its id is the id to PATCH
    // and its destinations are its own.
    const handleAttachToRoute = async (route: any) => {
        if (!keycloak.token) return;
        const route_id = route?.id;
        if (!route_id) {
            console.log("cannot attach: candidate has no route id", route);
            toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        try {
            const existing = (route.destinations || []).map((d: any) => d.id);
            const updated = await addRouteDestinations(keycloak.token, route_id, {
                destinations: Array.from(new Set([...existing, dest_id])) as string[],
            });
            // The API layer swallows a 401 and resolves undefined; the login modal is
            // already up, so say nothing rather than claiming the record was attached.
            if (!updated) return;
            toast.success(t("Connection updated"), { position: toast.POSITION.TOP_RIGHT });
            setRouteDrawerOpen(false);
            navigate(`/routes/${route_id}/configurations/account/${dest_id}`);
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            }
        }
    };

    // Capability decides which roles are on offer: pull to be a source, push to be
    // a destination. A pull-only technology cannot receive data, so attaching it to a
    // Route would produce a destination that can never work.
    const canBeDataSource = (destination.type?.actions || []).some((a: any) => a.type === "pull");
    const canBeDestination = (destination.type?.actions || []).some((a: any) => a.type === "push");

    // Reuses this very record as the Route's provider: one account, two roles,
    // one set of credentials. Destinations are chosen afterwards, on the flow map.
    // The new Route gets its own name — prefilled with the Connection's for
    // convenience, but decoupled: the user can (and often should) change it.
    const [dataSourceRouteName, setDataSourceRouteName] = useState("");
    const handleUseAsDataSource = async () => {
        if (!keycloak.token || creatingDataSource) return;
        const ownerId = destination.owner?.id;
        if (!ownerId) {
            toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            return;
        }
        setCreatingDataSource(true);
        try {
            const created = await createRoute(keycloak.token, {
                name: dataSourceRouteName.trim() || destination.name,
                owner: ownerId,
                data_providers: [destination.id],
                destinations: [],
            });
            setConfirmDataSource(false);
            navigate(`/routes/${created.id}/configurations`, { state: { highlightAddDestination: true } });
        } catch (err: any) {
            console.log(err);
            if (err?.response?.status !== 401) {
                toast.error(t("Something went wrong. Please try again or contact support."), { position: toast.POSITION.TOP_RIGHT });
            }
        } finally {
            setCreatingDataSource(false);
        }
    };

    const handlePageClick = (e: any) => {
        setOffset(e.selected * page_size);
        setLoadingRoutes(true);
    }

    const requestSortSelectedDestinationRoute = (orderBy: string, sortDirection: boolean) => {
        const ordering = sortDirection ? orderBy : "-" + orderBy;
        setOrderingString(ordering);
        setLoadingRoutes(true);
    }

    const onChangeSelectedDestination = (e: any) => {
        const attr: string = e.target.id
        setChangesMade(true);
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            [attr]: e.target.value
        }))
    }

    const onChangeSelectedDestinationOwner = (e: any) => {
        setChangesMade(true);
        setDestination((prevDestination: Destination) => ({
            ...prevDestination,
            "owner": {
                name: e.label,
                id: e.value,
                description: ""
            },
        }))
    }

    const handleTestAction = async (action_id: string, action_type: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setTestActionLoading(true);
            // The stored configuration for this action — sections are saved before
            // they can be tested, so there is no unsaved copy to prefer.
            const stored = destination.configurations.find((a: ConnectionConfiguration) => a.action.id === action_id);
            const form_data = stored?.data ?? {};
            try {
                let result = await testConnectionAction(token, destination.id, action_type, { config_overrides: Object(form_data) });
                if (result) {
                    setTestActionData(result.data);
                }
            } catch (err: any) {
                setTestActionData({ error: err?.response?.data?.detail || err?.message || "Server error. Please try again." });
            } finally {
                setTestActionLoading(false);
            }
        }
    }

    const formIsValid = () => {
        let form_errors: any = {};
        const shouldRequireUrl = !isHiddenUrlDestinationType(destination.type.name);
        const urlError = validateBaseUrl({
            value: destination.base_url, asSiteName: isEarthRanger, optional: !shouldRequireUrl, t,
        });
        if (urlError) form_errors.base_url = urlError;
        if (destination.name.length === 0) {
            form_errors.name = t("Name is required.");
        }
        setErrors(form_errors);
        return Object.keys(form_errors).length === 0;
    }

    // action: 'save' | 'saveAndClose'
    // Resolves true only when the save landed — the section-switch prompt uses it
    // to decide whether it is safe to move to the section the user asked for.
    const handleSaveDestination = async (e: React.FormEvent<Element>): Promise<boolean> => {
        if (!formIsValid()) return false;
        else {
            if (keycloak.token) {
                try {
                    const saved = await updateDestination(keycloak.token, destination.id, {
                        "base_url": isEarthRanger ? constructEarthRangerUrl(destination.base_url) : destination.base_url,
                        "enabled": destination.enabled,
                        "owner": destination.owner.id,
                        "name": destination.name
                    });
                    // A 401 is swallowed by the API layer (the login modal handles
                    // it) and comes back as undefined — that is not a save, so the
                    // edits stay put rather than being cleared behind a toast.
                    if (!saved) return false;
                    toast.success(`${t("Destination updated")}`, {
                        position: toast.POSITION.TOP_RIGHT
                    });
                    trackDestinationUpdated(destination.id, destination.type?.name, destination.owner?.name);
                    setChangesMade(false);
                    setReloadDestination(true);
                    return true;
                } catch (err: any) {
                    toast.error(t("Failed to save destination. Please try again."), { position: toast.POSITION.TOP_RIGHT });
                    return false;
                }
            }
        }
        return false;
    }

    function handleSearchOrgDropdown(e: any) {
        setOrgSearchInput(e);
        if (e.length > 2) loadOrganizationsData(e);
        else if (e.length === 0) loadOrganizationsData();
    }

    function handleSelectSubPage(e: string) {
        if (!dest_id) return;
        guardedGo(() => {
            if (e === 'details') {
                // Go to the base details page for this destination
                navigate(`/connections/${dest_id}`);
            } else {
                // Go to the subpage for this destination
                navigate(`/connections/${dest_id}/${e}`);
            }
        });
    }

    // The /routes/:route_id/:subpage/:dest_id(/configuration) routes
    // accept any :subpage string — validate it like ManageRouteDetails does
    if (notFound || !isValidRouteSubpage(subpage)) return <NotFoundPage />;

    return (
        <>
        <DestinationDetailsModal
            currentMemberIsAdmin={currentMemberIsAdmin}
            destination={destination}
            destinationTitle={destinationTitle}
            destSubPage={currentSubPage}
            pendingEditsRef={pendingEditsRef}
            onSelectSubPage={handleSelectSubPage}
            organizations={organizations}
            loadMoreOrgs={() => {}}
            orgInputChange={handleSearchOrgDropdown}
            routes={selectedDestRoutes}
            onToggleModal={onToggleModal}
            requestSort={requestSortSelectedDestinationRoute}
            loadingRoutes={loadingRoutes}
            onChangeSelectedDestination={onChangeSelectedDestination}
            onChangeSelectedDestinationOwner={onChangeSelectedDestinationOwner}
            onTestAction={handleTestAction}
            testActionLoading={testActionLoading}
            testActionResponse={testActionData}
            onSave={handleSaveDestination}
            onReloadDestination={() => { setChangesMade(false); setReloadDestination(true); }}
            providerRoutes={providerRoutes}
            providerRoleFailed={providerRoleFailed}
            destRoutesFailed={destRoutesFailed}
            canBeDataSource={canBeDataSource}
            canBeDestination={canBeDestination}
            providerRoleLoaded={providerRoleLoaded}
            onUseAsDataSource={() => { setDataSourceRouteName(destination.name); setConfirmDataSource(true); }}
            onAddToRoute={openRouteDrawer}
            changesMade={changesMade}
            errors={errors}
            onPageClicked={handlePageClick}
            totalCount={totalCount}
            pageLoaded={pageLoaded}
            take={page_size}
            offset={offset}
            scrollableContentRef={scrollableContentRef}
            authToken={token && typeof token === 'string' && !keycloak.isTokenExpired() ? token : undefined}
        />
        <UnsavedSectionPrompt prompt={sectionPrompt} onClose={() => setSectionPrompt(null)} />
        <AddToRouteDrawer
            isOpen={routeDrawerOpen}
            onClose={() => setRouteDrawerOpen(false)}
            candidates={routeCandidates}
            loading={loadingCandidates}
            onAttach={handleAttachToRoute}
            onSearch={(searchStr: string) => { setDrawerSearch(searchStr); loadRouteCandidates(searchStr, drawerWorkspaceFilter); }}
            hasMore={candidatesHaveMore}
            workspaceOptions={organizations.filter((o: Organization) => o.id).map((o: Organization) => ({ label: o.name, value: o.id as string }))}
            workspaceFilter={drawerWorkspaceFilter}
            onChangeWorkspaceFilter={(option) => { setDrawerWorkspaceFilter(option); loadRouteCandidates(drawerSearch, option); }}
        />
        {confirmDataSource && (
            <ModalShell
                title={t("Use {{name}} as a data source", { name: destination.name })}
                onClose={() => setConfirmDataSource(false)}
                footer={<>
                    <button type="button" className={buttonClasses.primary} disabled={creatingDataSource}
                        onClick={handleUseAsDataSource}>
                        {t("Create Route")}
                    </button>
                    <button type="button" className={buttonClasses.cancel} onClick={() => setConfirmDataSource(false)}>
                        {t("Cancel")}
                    </button>
                </>}>
                <p className="text-base text-off-black">
                    {t("This creates a Route with {{name}} as its source. The same account keeps its role as a destination and its credentials stay shared, and you choose where the data goes next.", { name: destination.name })}
                </p>
                <div className="mt-4 flex flex-col">
                    <label htmlFor="data_source_route_name" className="text-sm font-semibold text-off-black mb-1">{t("Route Name")}</label>
                    <input
                        id="data_source_route_name"
                        data-testid="ManageDestinationDetails-route-name-input"
                        value={dataSourceRouteName}
                        onChange={(e) => setDataSourceRouteName(e.target.value)}
                        className="w-full p-1.5 border rounded px-2 transition-colors duration-300 border-neutral-gray-200"
                    />
                </div>
            </ModalShell>
        )}
        </>
    )
}